import { FormattedMessage } from "react-intl";
import { Button } from "@/components/atoms/Button";
import { BookingSchedule } from "@/lib/types/schedule-types";
import { cn } from "@/lib/utils";
import { addDays } from "date-fns";
import { useFormContext } from "react-hook-form";
import {
  dateToBookingSchedule,
  defaultScheduleDay,
  validateScheduleDay,
} from "./schedule-utils";
import { ScheduleDayInputs, ScheduleDayInputsProps } from "./ScheduleDayInputs";

export type ScheduleInputFieldProps = Omit<ScheduleDayInputsProps, "name"> & {
  fullWidth?: boolean;
  testId?: string;
  name?: string;
  extendDayDisabled?: boolean;
  className?: string;
  dayPickerDataTestingId?: string;
};

export const ScheduleInputField = ({
  name = "schedule",
  extendDayDisabled = false,
  fullWidth = true,
  datePickerProps,
  timeSelectProps,
  getCalendarModifiers,
  getTimeOptions,
  testId = "schedule-input",
  locale = "en-US",
  className,
  dayPickerDataTestingId,
  minHours,
  ...containerProps
}: ScheduleInputFieldProps) => {
  const { watch, setValue } = useFormContext();

  const scheduleValue = watch(name) as BookingSchedule;
  const currentSchedule = scheduleValue?.length
    ? scheduleValue
    : [defaultScheduleDay];

  const lastDay = currentSchedule[currentSchedule.length - 1];

  const addDay = () => {
    const lastStartDate = new Date(
      lastDay.start.date?.y as number,
      (lastDay.start.date?.m as number) - 1,
      lastDay.start.date?.d,
    );

    const lastEndDate = new Date(
      lastDay.end.date?.y as number,
      (lastDay.end.date?.m as number) - 1,
      lastDay.end.date?.d,
    );

    setValue(name, [
      ...currentSchedule,
      {
        start: {
          ...lastDay.start,
          date: dateToBookingSchedule(addDays(lastStartDate, 1)),
        },
        end: {
          ...lastDay.end,
          date: dateToBookingSchedule(addDays(lastEndDate, 1)),
        },
      },
    ]);
  };

  const removeDay = () => {
    setValue(name, currentSchedule.slice(0, currentSchedule.length - 1));
  };

  return (
    <div
      className={cn(
        "tw-flex tw-flex-col tw-gap-16",
        {
          "tw-w-full": fullWidth,
        },
        className,
      )}
      {...containerProps}
    >
      {currentSchedule.map((_, i: number) => (
        <ScheduleDayInputs
          key={`${name}-${i}`}
          name={`${name}.${i}`}
          locale={locale}
          getCalendarModifiers={getCalendarModifiers}
          getTimeOptions={getTimeOptions}
          testId={`${testId}-day-${i}`}
          datePickerProps={datePickerProps}
          timeSelectProps={timeSelectProps}
          dayPickerDataTestingId={`${dayPickerDataTestingId}-${i}`}
          minHours={minHours}
        />
      ))}

      <div className="tw-flex tw-justify-between tw-items-center">
        <Button
          testId="extend-day-button"
          className="tw-body-1-long"
          variant="text-only"
          appearance="purple"
          onClick={addDay}
          disabled={extendDayDisabled || !validateScheduleDay(lastDay)}
        >
          <FormattedMessage defaultMessage="Extend a day" />
        </Button>

        {currentSchedule.length > 1 && (
          <Button
            className="tw-body-1-long"
            variant="text-only"
            onClick={removeDay}
          >
            <FormattedMessage defaultMessage="Remove" />
          </Button>
        )}
      </div>
    </div>
  );
};
